import { apiFetch } from '@app/lib/api'
import type { CariDetailResponse, CariFilter, CariListResponse } from '../../../worker/api/cari.types'

/**
 * Data layer for the cari module: typed fetch helpers + TanStack Query keys.
 * Response types come from the Worker's API contract (`worker/api/cari.types.ts`),
 * so the API and the screens share a single contract and can't drift apart.
 */

export type {
  CariBalance,
  CariCurrencyTotal,
  CariDetailResponse,
  CariDocSource,
  CariFilter,
  CariInvoiceRow,
  CariListResponse,
  CariListRow,
  CariLocationBalance,
  CariOpenOrderRow,
  CariStatementResponse,
  CariStatementRow,
  CariType,
} from '../../../worker/api/cari.types'

export interface CariListParams {
  query?: string
  tip?: CariFilter
  page?: number
  pageSize?: number
}

export interface CariDetailParams {
  currency?: string | null
  page?: number
  pageSize?: number
}

function listSearch(params: CariListParams): string {
  const search = new URLSearchParams()
  if (params.query) search.set('query', params.query)
  if (params.tip && params.tip !== 'all') search.set('tip', params.tip)
  if (params.page && params.page > 1) search.set('page', String(params.page))
  if (params.pageSize) search.set('pageSize', String(params.pageSize))
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

function detailSearch(params: CariDetailParams): string {
  const search = new URLSearchParams()
  if (params.currency) search.set('currency', params.currency)
  if (params.page && params.page > 1) search.set('page', String(params.page))
  if (params.pageSize) search.set('pageSize', String(params.pageSize))
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const cariApi = {
  list: (params: CariListParams) => apiFetch<CariListResponse>(`/cari${listSearch(params)}`),
  /**
   * `ckod` is a dotted code and can contain spaces — always encoded.
   *
   * `init` exists for the callers TanStack Query does not own: the print
   * flow walks the statement page by page in a plain loop and needs an
   * `AbortSignal` to stop mid-walk when the print is superseded or the screen
   * unmounts (see `cari-detail.tsx`).
   */
  detail: (ckod: string, params: CariDetailParams = {}, init?: RequestInit) =>
    apiFetch<CariDetailResponse>(`/cari/${encodeURIComponent(ckod)}${detailSearch(params)}`, init),
}

export const cariQueryKeys = {
  all: ['cari'] as const,
  list: (params: CariListParams) => ['cari', 'list', params] as const,
  detail: (ckod: string, params: CariDetailParams) => ['cari', 'detail', ckod, params] as const,
}
